import {hasPermission, isSelectiveAiDomains} from 'common/HelperFunctions'
import {USER_TYPES_NUMERIC} from 'common/constants/user'
import React, {useCallback, useEffect, useMemo, useRef} from 'react'
import {IntlShape, useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {useLocation, useNavigate} from 'react-router'
import {RootState} from 'setup'

import {getTheme} from '../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'
import {useAuth} from '../../../modules/auth'
import ChatbotTrigger from '../ChatbotTrigger.tsx'
import ChatbotWidget from '../ChatbotWidget.tsx'
import {ChatbotMode} from '../components/config/chatbotModeConfig'
import {useChatbotModalContext} from '../hooks/ChatbotModalContext'
import {usePageContextReader} from '../pageContext/PageContextStore'
import {collectScreenSnapshot} from '../pageContext/collectScreenSnapshot'
import {ChatPageContext, ScreenSnapshot} from '../pageContext/pageContextTypes'
import {releaseChatbotTextSendLock} from '../utils/chatbotSendGuard'
import {getAssistantCapabilities} from './assistant/assistantCapabilities'
import {
  AssistantChatSession,
  AssistantSessionSurface,
  assistantSessionOwner,
  clearAllAssistantSessions,
  clearAssistantSession,
  loadAssistantSession,
  peekAssistantSession,
  releaseApplicantSessionOnOwnerChange,
  saveAssistantSession,
} from './assistant/assistantChatSession'
import {
  hasSeenAssistantChatbotIntro,
  markAssistantChatbotIntroSeen,
} from './assistant/assistantChatbotIntro'
import {
  getSupportWelcomeSuggestions,
  getSupportWelcomeSuggestionsForAssistant,
} from './assistant/assistantWelcomeSuggestions'
import {getChatSurface, isScopedAdmissionSurface} from './assistant/chatSurface'
import {useAssistantChat} from './assistant/useAssistantChat'
import {useChatViewer} from './assistant/useChatViewer'
import {AttendanceChatMessage, ConversationHistoryItem} from './attendance/attendanceWizardTypes'

const peekedSessions: Record<AssistantSessionSurface, AssistantChatSession | null> = {
  general: peekAssistantSession('general'),
  admission_admin: peekAssistantSession('admission_admin'),
  admission_applicant: peekAssistantSession('admission_applicant'),
}

/** Header subtitle per scoped surface; the general one keeps the widget's own default. */
const SUBTITLE_FOR_SURFACE: Partial<Record<AssistantSessionSurface, string>> = {
  admission_admin: 'CHATBOT.ASSISTANT.ADMISSION_ADMIN.SUBTITLE',
  admission_applicant: 'CHATBOT.ASSISTANT.ADMISSION_APPLICANT.SUBTITLE',
}

/** How long the first-visit "New" badge stays on the launcher before it quiets down. */
const INTRO_HIGHLIGHT_DURATION_MS = 60_000

type AssistantChatbotPageProps = {
  /** Attendance board available (hostname/tenant gate). */
  attendanceEnabled: boolean
  /** Knowledge-base board available (`knowledge_base` permission). */
  faqEnabled: boolean
  /** Recurring daily attention pulse driven by ChatbotHost. */
  dailyHighlight?: boolean
}

type UseSurfaceConversationParams = {
  intl: IntlShape
  surface: AssistantSessionSurface
  getPageContext?: () => ChatPageContext | null
  welcomeMessageId?: string
}

/**
 * One surface's conversation: its own messages, history and `useAssistantChat` (so its own
 * typing state and request session). Two of these rather than one hook with swapped setters —
 * the send loop appends history into a ref, and a reply must land in the conversation that asked.
 */
function useSurfaceConversation({
  intl,
  surface,
  getPageContext,
  welcomeMessageId,
}: UseSurfaceConversationParams) {
  const [messages, setMessages] = React.useState<AttendanceChatMessage[]>(
    () => peekedSessions[surface]?.messages ?? []
  )
  const [conversationHistory, setConversationHistory] = React.useState<ConversationHistoryItem[]>(
    () => peekedSessions[surface]?.conversationHistory ?? []
  )

  const chat = useAssistantChat({
    intl,
    messages,
    setMessages,
    conversationHistory,
    setConversationHistory,
    surface,
    getPageContext,
    welcomeMessageId,
  })

  return {messages, setMessages, conversationHistory, setConversationHistory, ...chat}
}

function persistConversation(
  userId: string,
  surface: AssistantSessionSurface,
  messages: AttendanceChatMessage[],
  conversationHistory: ConversationHistoryItem[]
) {
  if (messages.length === 0) {
    clearAssistantSession(surface)
    return
  }
  saveAssistantSession({userId, messages, conversationHistory}, surface)
}

/**
 * The single "Versi AI Assistant" widget: answers attendance questions and how-to questions
 * from the user manual. Which boards it can reach is decided by the caller; message routing
 * lives in `useAssistantChat`.
 *
 * Staff admission pages get a conversation of their own, scoped by the backend to admission
 * how-tos (`options.pageContext`). The widget stays open across navigation and shows whichever
 * conversation belongs to the page.
 */
const AssistantChatbotPage: React.FC<AssistantChatbotPageProps> = ({
  attendanceEnabled,
  faqEnabled,
  dailyHighlight = false,
}) => {
  const intl = useIntl()
  const theme = getTheme()
  const navigate = useNavigate()
  const {pathname} = useLocation()
  const {currentUser} = useAuth()
  const reduxUser = useSelector((state: RootState) => state.userReducer.user)
  const userId =
    currentUser?.id != null
      ? String(currentUser.id)
      : reduxUser?.id != null
        ? String(reduxUser.id)
        : undefined
  const {isOpen, isFullscreen, openChatbot, closeChatbot, toggleFullscreen} =
    useChatbotModalContext()

  // The widget's attendance mode carries every assistive control (date pickers,
  // disambiguation, exports); the support-only variant needs none of that.
  const chatbotMode: ChatbotMode = attendanceEnabled ? 'attendance' : 'faq'

  // The admission pages each keep a conversation of their own for students, parents, applicants
  // and visitors; ADMIN / TEACHER / STAFF always get the general one (no pageContext, no dom).
  const viewer = useChatViewer()
  const activeSurface: AssistantSessionSurface = getChatSurface(pathname, viewer)
  const isAdmissionSurface = isScopedAdmissionSurface(activeSurface)

  // Built when a message is sent: the page's own manifest plus the labels on screen right then.
  // Pathname only — a query string can carry applicant ids.
  const readPageManifest = usePageContextReader()
  const getAdmissionPageContext = useCallback((): ChatPageContext | null => {
    if (!isAdmissionSurface) return null
    const manifest = readPageManifest()
    let dom: ScreenSnapshot | null = null
    try {
      dom = collectScreenSnapshot()
    } catch {
      // A page the collector cannot read still gets its question answered.
    }
    return {
      surface: activeSurface,
      path: pathname,
      ...(manifest?.page ? {page: manifest.page} : {}),
      ...(manifest?.visible ? {visible: manifest.visible} : {}),
      ...(dom ? {dom} : {}),
    }
  }, [activeSurface, isAdmissionSurface, pathname, readPageManifest])

  const general = useSurfaceConversation({intl, surface: 'general'})
  const admission = useSurfaceConversation({
    intl,
    surface: 'admission_admin',
    getPageContext: getAdmissionPageContext,
    welcomeMessageId: 'CHATBOT.ASSISTANT.ADMISSION_ADMIN.WELCOME',
  })
  const applicant = useSurfaceConversation({
    intl,
    surface: 'admission_applicant',
    getPageContext: getAdmissionPageContext,
    welcomeMessageId: 'CHATBOT.ASSISTANT.ADMISSION_APPLICANT.WELCOME',
  })
  const active =
    activeSurface === 'admission_applicant'
      ? applicant
      : activeSurface === 'admission_admin'
        ? admission
        : general

  const {
    messages: generalMessages,
    setMessages: setGeneralMessages,
    conversationHistory: generalHistory,
    setConversationHistory: setGeneralHistory,
  } = general
  const {
    messages: admissionMessages,
    setMessages: setAdmissionMessages,
    conversationHistory: admissionHistory,
    setConversationHistory: setAdmissionHistory,
  } = admission
  const {
    messages: applicantMessages,
    setMessages: setApplicantMessages,
    conversationHistory: applicantHistory,
    setConversationHistory: setApplicantHistory,
  } = applicant
  const {
    messages,
    setMessages,
    conversationHistory: activeHistory,
    setConversationHistory,
    initializeWelcome,
    resetAssistiveState,
    handleSendMessage,
    isTyping,
  } = active

  const [sessionHydrated, setSessionHydrated] = React.useState(false)
  const [showIntroHighlight, setShowIntroHighlight] = React.useState(false)

  const prevIsOpenRef = useRef(false)
  const hasHydratedRef = useRef(false)

  // Attendance builds its live example chips inside the widget; these are appended after
  // them (or stand alone when the attendance board is off). Admission pages show the
  // welcome message alone, with no chips.
  const welcomeSuggestions = useMemo(() => {
    if (!faqEnabled || isAdmissionSurface) return undefined
    const t = (id: string) => intl.formatMessage({id})
    const isStudent = currentUser?.type === USER_TYPES_NUMERIC.STUDENT
    return attendanceEnabled
      ? getSupportWelcomeSuggestionsForAssistant(t, {isStudent})
      : getSupportWelcomeSuggestions(t, {isStudent})
  }, [attendanceEnabled, currentUser?.type, faqEnabled, intl, isAdmissionSurface])

  // What this user's assistant can actually do, stated once on first open. Nothing on admission
  // pages: the capabilities it would list are refused there.
  const welcomeCapabilities = useMemo(
    () =>
      isAdmissionSurface
        ? []
        : getAssistantCapabilities((id: string) => intl.formatMessage({id}), {
            attendanceEnabled,
            faqEnabled,
          }),
    [attendanceEnabled, faqEnabled, intl, isAdmissionSurface]
  )

  // The applicant pages are public, so their conversation can belong to nobody in particular
  // ('anonymous'). The general and staff conversations only ever belong to a signed-in user.
  const sessionOwner = assistantSessionOwner(userId)
  const sessionOwnerRef = useRef<string | null>(null)

  const persistSession = useCallback(() => {
    if (!hasHydratedRef.current) return
    if (userId) {
      persistConversation(userId, 'general', generalMessages, generalHistory)
      persistConversation(userId, 'admission_admin', admissionMessages, admissionHistory)
    }
    persistConversation(sessionOwner, 'admission_applicant', applicantMessages, applicantHistory)
  }, [
    userId,
    sessionOwner,
    generalMessages,
    generalHistory,
    admissionMessages,
    admissionHistory,
    applicantMessages,
    applicantHistory,
  ])

  useEffect(() => {
    // Signing in (or a different user) must never inherit the applicant conversation someone
    // else had on the public pages: drop it from storage and from state before restoring.
    if (releaseApplicantSessionOnOwnerChange(sessionOwnerRef.current, sessionOwner)) {
      setApplicantMessages([])
      setApplicantHistory([])
      peekedSessions.admission_applicant = null
    }
    sessionOwnerRef.current = sessionOwner

    const conversations = [
      {surface: 'general', setMessages: setGeneralMessages, setHistory: setGeneralHistory},
      {
        surface: 'admission_admin',
        setMessages: setAdmissionMessages,
        setHistory: setAdmissionHistory,
      },
      {
        surface: 'admission_applicant',
        setMessages: setApplicantMessages,
        setHistory: setApplicantHistory,
      },
    ] as const

    let someoneElse = false
    conversations.forEach(({surface, setMessages: setSurfaceMessages, setHistory}) => {
      const owner = surface === 'admission_applicant' ? sessionOwner : userId
      if (!owner) {
        // Signed out: nothing but the applicant conversation is ours to show.
        setSurfaceMessages([])
        setHistory([])
        return
      }
      const peeked = peekedSessions[surface]
      const saved = loadAssistantSession(owner, surface)
      if (saved) {
        setSurfaceMessages(saved.messages)
        setHistory(saved.conversationHistory)
      } else if (peeked && peeked.userId !== owner) {
        setSurfaceMessages([])
        setHistory([])
        someoneElse = true
      }
    })
    if (someoneElse) clearAllAssistantSessions()

    hasHydratedRef.current = true
    setSessionHydrated(true)
  }, [
    userId,
    sessionOwner,
    setGeneralMessages,
    setGeneralHistory,
    setAdmissionMessages,
    setAdmissionHistory,
    setApplicantMessages,
    setApplicantHistory,
  ])

  useEffect(() => {
    const show = !hasSeenAssistantChatbotIntro(userId)
    setShowIntroHighlight(show)
    if (!show) return undefined
    // The "New" badge announces the launcher; it must not hang over the dashboard for the
    // whole session when the user never opens it. It comes back on the next load until the
    // first open marks the intro as seen.
    const timer = window.setTimeout(() => setShowIntroHighlight(false), INTRO_HIGHLIGHT_DURATION_MS)
    return () => window.clearTimeout(timer)
  }, [userId])

  useEffect(() => {
    if (!sessionHydrated) return
    persistSession()
  }, [sessionHydrated, persistSession])

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (applicantMessages.length > 0) {
        saveAssistantSession(
          {
            userId: sessionOwner,
            messages: applicantMessages,
            conversationHistory: applicantHistory,
          },
          'admission_applicant'
        )
      }
      if (!userId) return
      if (generalMessages.length > 0) {
        saveAssistantSession({
          userId,
          messages: generalMessages,
          conversationHistory: generalHistory,
        })
      }
      if (admissionMessages.length > 0) {
        saveAssistantSession(
          {userId, messages: admissionMessages, conversationHistory: admissionHistory},
          'admission_admin'
        )
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [
    userId,
    sessionOwner,
    generalMessages,
    generalHistory,
    admissionMessages,
    admissionHistory,
    applicantMessages,
    applicantHistory,
  ])

  // Welcome is level-triggered (open + hydrated + empty), not open-edge only: the
  // header Support button can open the widget before hydration lands, and an edge
  // check left the chat on the empty state instead of greeting. It also greets on the
  // first visit to an admission page with the widget already open.
  useEffect(() => {
    const justOpened = isOpen && !prevIsOpenRef.current
    if (justOpened) {
      resetAssistiveState()
    }
    if (isOpen && sessionHydrated && messages.length === 0) {
      initializeWelcome()
    }
    prevIsOpenRef.current = isOpen
  }, [isOpen, initializeWelcome, messages.length, resetAssistiveState, sessionHydrated])

  useEffect(() => {
    if (prevIsOpenRef.current && !isOpen) {
      persistSession()
    }
  }, [isOpen, persistSession])

  const handleClose = useCallback(() => {
    resetAssistiveState()
    try {
      releaseChatbotTextSendLock()
    } catch {
      /* ignore */
    }
    persistSession()
    closeChatbot()
  }, [resetAssistiveState, closeChatbot, persistSession])

  // Starts over only the conversation on screen.
  const handleNewChat = useCallback(() => {
    clearAssistantSession(activeSurface)
    setMessages([])
    setConversationHistory([])
    resetAssistiveState()
    try {
      releaseChatbotTextSendLock()
    } catch {
      /* ignore */
    }
    initializeWelcome()
  }, [activeSurface, initializeWelcome, resetAssistiveState, setConversationHistory, setMessages])

  const toggleWidget = () => {
    if (isOpen) {
      handleClose()
    } else {
      markAssistantChatbotIntroSeen(userId)
      setShowIntroHighlight(false)
      openChatbot()
    }
  }

  // One-click handoff from a support answer to the ticket manager. Same gate as the
  // `support-tickets` route: on the AI domains it is open without `tickets_manager`, and the
  // backend's "click Create a support ticket below" reply must not arrive without the button.
  const canCreateTicket =
    faqEnabled &&
    !isAdmissionSurface &&
    (hasPermission('tickets_manager') || isSelectiveAiDomains())
  const handleCreateTicket = useCallback(() => {
    handleClose()
    navigate('/institute/support-tickets')
  }, [handleClose, navigate])

  return (
    <>
      {!isOpen && (
        <ChatbotTrigger
          onClick={toggleWidget}
          unreadCount={0}
          mode={chatbotMode}
          highlight={showIntroHighlight || dailyHighlight}
        />
      )}

      <div style={{display: isOpen ? 'block' : 'none'}} aria-hidden={!isOpen}>
        <ChatbotWidget
          keepMounted
          isOpen={isOpen}
          isFullscreen={isFullscreen}
          onClose={handleClose}
          onToggleFullscreen={toggleFullscreen}
          messages={messages}
          onSendMessage={handleSendMessage}
          isTyping={isTyping}
          theme={theme}
          currentMenuItems={[]}
          onMenuSelect={() => {}}
          menuStack={[]}
          menuState={{
            isMenuOpen: false,
            isSubmenuOpen: false,
            isConversationStarted: true,
          }}
          onMenuStateChange={() => {}}
          hideMenu
          onOptionSelect={(value, extras) => handleSendMessage(value, extras)}
          allowVoice={false}
          chatbotMode={chatbotMode}
          onToggleTheme={() => {}}
          onNewChat={handleNewChat}
          welcomeSuggestions={welcomeSuggestions}
          welcomeCapabilities={welcomeCapabilities}
          onCreateTicket={canCreateTicket ? handleCreateTicket : undefined}
          surface={activeSurface}
          hideAttendanceAffordances={isAdmissionSurface}
          lastAnswerCapability={activeHistory[activeHistory.length - 1]?.capability ?? null}
          subtitleMessageId={SUBTITLE_FOR_SURFACE[activeSurface]}
        />
      </div>
    </>
  )
}

export default AssistantChatbotPage
